import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Slate, Editable, withReact } from "slate-react";
import { createEditor, Transforms, Editor, Node } from "slate";
import { withHistory } from "slate-history";
import styled, { keyframes } from "styled-components";
import { Tooltip } from "@material-ui/core";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { shortTimecode } from "../TimeCodeConverter";

const initialValue = [
  {
    type: "timedText",
    speaker: "none",
    start: 0,
    end: 0,
    children: [{ text: "Transcription started..." }],
  },
];

const LiveTranscript = ({
  caseId,
  data,
  streamingKey,
  timecodeOffset = 0,
  currentTime = 0,
  showTimecodes = true,
  showSpeakers = true,
  handleWordClick,
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const [value, setValue] = useState(initialValue);
  const [interimText, setInterimText] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const containerRef = useRef(null);
  const apiLoadedRef = useRef(null);
  const lastWordEndRef = useRef(0);
  const processedDataRef = useRef(null);
  const paragraphCounterRef = useRef(0);

  const updateValue = useCallback(() => {
    setValue([...editor.children]);
  }, [editor]);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 20);
    }
  }, []);

  const normalizeData = (data = []) => {
    return data
      .map((item, index) => {
        let textValue = "";

        if (Array.isArray(item.text)) {
          textValue = item.text.map((t) => t.text || "").join(" ");
        } else if (typeof item.text === "string") {
          textValue = item.text;
        } else if (item.text?.text) {
          textValue = item.text.text;
        }

        if (!textValue.trim()) return null;

        return {
          id: item.id ?? index + 1,
          start: Number(item.start ?? 0),
          end: Number(item.end ?? item.start ?? 0),
          confidence: item.confidence ?? "",
          speaker: item.speaker || "Unknown",
          text: [
            {
              text: textValue.trim(),
            },
          ],
        };
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (!caseId || !editor) return;

    if (processedDataRef.current === data) return;

    const normalized = normalizeData(data);

    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    paragraphCounterRef.current = 0;

    let lastEnd = 0;
    const nodes = [];

    if (normalized.length > 0) {
      for (const item of normalized) {
        const textValue = item.text[0].text;

        const start = item.start;
        const end = item.end;
        const speaker = item.speaker;

        lastEnd = Math.max(lastEnd, end);
        paragraphCounterRef.current += 1;

        nodes.push({
          type: "timedText",
          id: paragraphCounterRef.current,
          speaker,
          speakerName: speaker,
          start,
          end,
          previousTimings: `${start} ${end}`,
          isFinalCount: 1,
          children: [{ text: textValue }],
        });
      }
    }

    if (nodes.length === 0) {
      nodes.push({
        type: "paragraph",
        children: [{ text: "" }],
      });
    }

    Transforms.insertNodes(editor, nodes, { at: [0] });

    lastWordEndRef.current = lastEnd;
    processedDataRef.current = data;
    updateValue();
    apiLoadedRef.current = true;
    setApiLoaded(true);

    requestAnimationFrame(scrollToBottom);
  }, [caseId, data, editor, updateValue, scrollToBottom]);

  const groupWordsBySpeaker = (words) => {
    const groups = [];
    let currentGroup = null;

    words.forEach((w) => {
      const speaker = w.speaker ?? "Speaker";
      const text = w.punctuated_word ?? w.word;

      if (!currentGroup || currentGroup.speaker !== speaker) {
        currentGroup = {
          speaker,
          start: w.start,
          end: w.end,
          text,
          isFinalCount: 1,
        };
        groups.push(currentGroup);
      } else {
        currentGroup.text += " " + text;
        currentGroup.end = w.end;
      }
    });

    return groups;
  };

  useEffect(() => {
    if (!caseId || !apiLoaded || !streamingKey) return;

    const ws = new WebSocket("wss://live-stream-transcription.justact.co.in");

    ws.onopen = () => {
      console.log("WebSocket connected for live transcription updates");
      setIsConnected(true);
      ws.send(
        JSON.stringify({
          type: "subscribe",
          streamKey: streamingKey,
        }),
      );
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        const totalLatencyMs = payload?.latencyMs ?? null;

        const words = payload?.data?.channel?.alternatives?.[0]?.words || [];
        const isFinal = payload?.is_final;
        if (!words.length) return;
        const newWords = words.filter((w) => w.end > lastWordEndRef.current);

        if (!newWords.length) return;

        const speakerGroups = groupWordsBySpeaker(newWords);

        if (!isFinal) {
          const lastGroup = speakerGroups[speakerGroups.length - 1];

          setInterimText({
            text: lastGroup.text,
            speaker: lastGroup.speaker,
            start: lastGroup.start,
            end: lastGroup.end,
            latencyMs: totalLatencyMs,
          });

          requestAnimationFrame(scrollToBottom);
          return;
        }

        lastWordEndRef.current = newWords[newWords.length - 1].end;

        Editor.withoutNormalizing(editor, () => {
          speakerGroups.forEach(
            ({ speaker, text, start, end, isFinalCount }) => {
              const lastIndex = editor.children.length - 1;
              const lastNode =
                lastIndex >= 0 ? editor.children[lastIndex] : null;
              const prevCount = lastNode?.isFinalCount ?? 0;

              const isSameSpeaker =
                !!lastNode &&
                lastNode.type === "timedText" &&
                lastNode.speaker === speaker &&
                !lastNode.speechFinal &&
                !lastNode.utteranceEnd;

              if (isSameSpeaker && lastIndex >= 0 && prevCount < 3) {
                Transforms.insertText(editor, " " + text, {
                  at: Editor.end(editor, [lastIndex]),
                });
                Transforms.setNodes(
                  editor,
                  { end, isFinalCount: prevCount + 1 },
                  { at: [lastIndex] },
                );
              } else {
                paragraphCounterRef.current += 1;
                Transforms.insertNodes(editor, {
                  type: "timedText",
                  id: paragraphCounterRef.current,
                  speaker,
                  speakerName: `Speaker ${Number(speaker) + 1}`,
                  start,
                  end,
                  previousTimings: `${start} ${end}`,
                  isFinalCount: 1,
                  children: [{ text }],
                });
              }
            },
          );
        });

        setValue(editor.children);
        setInterimText(null);
        requestAnimationFrame(scrollToBottom);
      } catch (err) {
        console.error("WebSocket message parsing error:", err);
      }
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return () => {
      ws.close();
    };
  }, [caseId, apiLoaded, streamingKey, editor, scrollToBottom, setValue]);

  const getHighlightKey = useCallback(() => {
    if (!currentTime) return "";
    for (const node of editor.children) {
      const s = Number(node.start);
      const e = Number(node.end);
      if (currentTime >= s && currentTime <= e) return node.previousTimings;
    }
    return "";
  }, [currentTime, editor.children]);

  const renderElement = useCallback(
    (props) => {
      if (props.element.type === "timedText") {
        return (
          <TimedTextElement
            {...props}
            timecodeOffset={timecodeOffset}
            handleWordClick={handleWordClick}
            showSpeakers={showSpeakers}
            showTimecodes={showTimecodes}
          />
        );
      }
      return <p {...props.attributes}>{props.children}</p>;
    },
    [timecodeOffset, handleWordClick, showSpeakers, showTimecodes],
  );

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    const parent = children.props?.parent;
    const style = leaf.color
      ? { backgroundColor: leaf.color, borderRadius: "4px", padding: "2px 4px" }
      : {};

    if (leaf.suggestionText || leaf.name) {
      return (
        <Tooltip
          title={
            leaf.name
              ? `${leaf.suggestionText || leaf.role} - ${leaf.name}`
              : leaf.suggestionText
          }
          placement="top"
          arrow
        >
          <HighlightedText
            className="timecode text"
            style={style}
            data-start={parent?.start}
            data-previous-timings={parent?.previousTimings}
            {...attributes}
          >
            {children}
          </HighlightedText>
        </Tooltip>
      );
    }

    return (
      <HighlightedText
        className="timecode text"
        style={style}
        data-start={parent?.start}
        data-previous-timings={parent?.previousTimings}
        {...attributes}
      >
        {children}
      </HighlightedText>
    );
  }, []);

  return (
    <TranscriptWrapper>
      <LiveStatusBar>
        <StatusIndicator $connected={isConnected}>
          <StatusDot $connected={isConnected} />
          {isConnected ? "Live" : "Connecting…"}
        </StatusIndicator>

        <SegmentCount>
          {isConnected ? editor.children.length : 0}{" "}
          {editor.children.length === 1 ? "segment" : "segments"}
        </SegmentCount>
      </LiveStatusBar>

      <style scoped>{`
        .timecode[data-previous-timings*="${getHighlightKey()}"] {
          background: ${COLORS.BTN_GREEN}18;
          color: ${COLORS.BTN_GREEN};
          font-weight: 600;
          padding: 2px 5px;
          border-radius: 4px;
        }
      `}</style>

      <TranscriptContent ref={containerRef}>
        <Slate editor={editor} value={value}>
          <StyledEditable
            readOnly
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.selection = null;
            }}
          />

          {interimText && (
            <InterimBlock>
              <InterimHeader>
                {showSpeakers && (
                  <SpeakerBadge $interim>
                    <SpeakerName>
                      {`Speaker ${
                        Number(
                          String(interimText?.speaker).match(/\d+/)?.[0] || 0,
                        ) + 1
                      }`}
                    </SpeakerName>
                  </SpeakerBadge>
                )}
                {showTimecodes && (
                  <TimecodeChip $interim>
                    {shortTimecode(interimText.start)}
                  </TimecodeChip>
                )}
                {interimText?.latencyMs && (
                  <LatencyBadge>{interimText.latencyMs} ms</LatencyBadge>
                )}
              </InterimHeader>

              <InterimText>
                <TypingCursor>•</TypingCursor>
                {interimText.text}
              </InterimText>
            </InterimBlock>
          )}
        </Slate>
      </TranscriptContent>
    </TranscriptWrapper>
  );
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

export const TranscriptWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
`;

const LiveStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${(p) => (p.$connected ? "#059669" : "#d97706")};
`;

const StatusDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) => (p.$connected ? "#10b981" : "#f59e0b")};
  animation: ${(p) => (p.$connected ? pulse : "none")} 2.2s infinite;
`;

const SegmentCount = styled.div`
  background: #f1f5f9;
  padding: 4px 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
`;

export const TranscriptContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const StyledEditable = styled(Editable)`
  min-height: 100%;
`;

export const TranscriptBlock = styled.div`
  background: white;
  // border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 12px;
  transition: all 0.18s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
`;

const InterimBlock = styled.div`
  background: #fffef5;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 12px;
  animation: ${slideUp} 0.3s ease;
`;

const InterimHeader = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  font-size: 13px;
`;

export const SpeakerBadge = styled.div`
  display: inline-flex;
  align-items: center;
  // background: ${(p) => (p.$interim ? "#fbbf24" : "#91C4C3")};
  color: ${(p) => (p.$interim ? "#d97706" : "#00838c")};
  border-radius: 999px;
  font-size: 15px;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};

  &:hover {
    text-decoration: underline;
  }
`;

export const TimecodeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) => (p.$interim ? "#fef3c7" : "#ecfdf5")};
  color: ${(p) => (p.$interim ? "#d97706" : "#00838c")};
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 6px;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
`;

const LatencyBadge = styled.div`
  background: #eff6ff;
  color: #1d4ed8;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
`;

const InterimText = styled.div`
  color: #78350f;
  font-size: 15.5px;
  line-height: 1.65;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const TypingCursor = styled.span`
  width: 5px;
  animation: blink 1.4s step-end infinite;
  color: #f59e0b;
  font-weight: bold;
`;

const HighlightedText = styled.span`
  transition: background 0.15s;
  &:hover {
    background: rgba(16, 185, 129, 0.12);
  }
`;

const TimedTextElement = ({
  element,
  attributes,
  children,
  timecodeOffset,
  handleWordClick,
  showSpeakers,
  showTimecodes,
}) => {
  const isEmpty = !Node.string(element).trim();
  if (isEmpty) return null;

  const displayTime = (element.start || 0) + timecodeOffset;

  const s = String(element?.speaker || "").trim();
  const m = s.match(/\d+/);
  const speakerLabel = m ? `Speaker ${m[0]}` : s || "Speaker";

  return (
    <TranscriptBlock>
      <MetadataBar {...attributes}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {showSpeakers && speakerLabel && (
            <SpeakerBadge>{speakerLabel}</SpeakerBadge>
          )}

          {showTimecodes && (
            <TimecodeChip onClick={() => handleWordClick(displayTime)}>
              {shortTimecode(displayTime)}
            </TimecodeChip>
          )}
        </div>

        <div
          style={{
            fontSize: "11px",
            fontFamily: theme.fonts.primaryFontSemiBold,
            color: "#64748b",
          }}
        >
          {element?.id}
        </div>
      </MetadataBar>

      <TextContent onDoubleClick={() => handleWordClick(displayTime)}>
        {children}
      </TextContent>
    </TranscriptBlock>
  );
};

export const MetadataBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 10px;
`;

export const TextContent = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-size: 15.5px;
  line-height: 1.68;
  cursor: text;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SpeakerName = styled.span`
  // color: white;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  font-family: ${theme.fonts.primaryFontSemiBold};

  @media ${theme.breakpoints.sm_down} {
    font-size: 13px;
    max-width: 100px;
  }
`;

export default LiveTranscript;
